import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

import { Alert, Loading } from '../../components';
import { Badge } from '../../components/ui';
import { getFileUrl } from '../../core/config/api.js';
import AddProductForm from '../../product/components/AddProduct/AddProductForm';

import './product-submission-modal.scss';

/**
 * The submitted product form (store-catalog feature), opened from Owner Admin →
 * My Products and from the master Catalog Approvals queue. While the submission
 * is pending/rejected it renders the real product form (AddProductForm in edit
 * mode → PUT /api/store-catalog/products/{id}) — the owner store's admins and
 * RC masters may edit until it is approved; editing a rejected one resubmits it
 * as pending. Once approved it renders READ-ONLY (fields + image thumbnails),
 * because an approved row may already be referenced by listings.
 *
 * Note this renders through a portal to document.body, so it sits OUTSIDE
 * `.rc-app-shell__content` — every legacy dark-mode rule is scoped under that
 * class, which is why the form styling has to be re-declared for `.psm` in
 * product-submission-modal.scss (white-on-white fields otherwise).
 */

const STATUS_TONE = { approved: 'success', pending: 'warning', rejected: 'danger' };

const prettyLabel = (name, labels) =>
  labels?.[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function ProductSubmissionModal({ productId, categoryId, onClose, onChanged }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedNote, setSavedNote] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`/api/store-catalog/products/${productId}`);
        if (alive) setProduct(res.data);
      } catch (e) {
        const payload = e?.response?.data;
        if (alive) {
          setError(
            payload?.message
              || Object.values(payload?.errors || {}).flat().join(' ')
              || 'Could not load this submission.'
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [productId]);

  const editable = product && product.approval_status !== 'approved';

  const handleSaved = (updated) => {
    setProduct(updated);
    setSavedNote(
      updated.approval_status === 'pending'
        ? 'Saved — the submission is back in the Retail Circle approval queue.'
        : 'Saved.'
    );
    if (typeof onChanged === 'function') onChanged(updated);
  };

  const title = product
    ? `${product.brand || ''} ${product.model || ''}`.trim() || 'Product submission'
    : 'Product submission';

  return createPortal(
    <div className="psm__overlay" onMouseDown={onClose}>
      <div className="psm" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="psm__header">
          <h3>
            {title}
            {product && (
              <Badge tone={STATUS_TONE[product.approval_status] || 'muted'} className="psm__status">
                {product.approval_status}
              </Badge>
            )}
          </h3>
          <button type="button" className="psm__close" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className="psm__body">
          {loading && <Loading />}
          {!loading && error && <Alert type="error" message={error} />}

          {!loading && product && product.approval_status === 'rejected' && product.rejection_reason && (
            <Alert
              type="error"
              message="Rejected by Retail Circle"
              description={`${product.rejection_reason} — saving your changes resubmits it for review.`}
            />
          )}
          {savedNote && <Alert type="success" message={savedNote} />}

          {!loading && product && editable && (
            <>
              <p className="psm__hint">
                Edit any field, then press <strong>Save changes</strong> at the bottom of the form.
                Changes are only stored when you save.
              </p>
              <div className="psm__form add-new-product">
                <AddProductForm
                  categoryId={categoryId || product.category_id}
                  product={product}
                  submitLabel="Save changes"
                  onSuccess={handleSaved}
                />
              </div>
            </>
          )}

          {!loading && product && !editable && (
            <div className="psm__details">
              <p className="psm__locked-note">
                This entry is approved and live for your store, so it can no longer be edited here —
                contact Retail Circle for changes.
              </p>

              <dl>
                <dt>Brand</dt><dd>{product.brand || '—'}</dd>
                <dt>Model</dt><dd>{product.model || '—'}</dd>
                <dt>Reference</dt><dd>{product.reference_number || '—'}</dd>
                {Object.entries(product.values || {})
                  .filter(([, value]) => value !== null && value !== '')
                  .map(([name, value]) => (
                    <React.Fragment key={name}>
                      <dt>{prettyLabel(name, product.field_labels)}</dt>
                      <dd>{String(value)}</dd>
                    </React.Fragment>
                  ))}
              </dl>

              <div className="psm__images">
                <span className="psm__images-label">Images</span>
                {product.images?.length ? (
                  <div className="psm__thumbs">
                    {product.images.map((fileId) => (
                      <img key={fileId} src={getFileUrl(fileId)} alt="" loading="lazy" />
                    ))}
                  </div>
                ) : (
                  <span className="psm__images-empty">No images</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProductSubmissionModal;
